import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { Plus, Search, Users2, AlertCircle, UserCheck, Trash2, AlertTriangle, FileDown } from "lucide-react";
import { ContractShiftsEditor } from "@/components/ContractShiftsEditor";
import { avgDailyHours } from "@/lib/contractShifts";
import { useAuth } from "@/contexts/AuthContext";

export const CLIENT_CATEGORIES = [
  { value: "empresas_industrias", label: "Empresas e industrias", badge: "blue" },
  { value: "barrio", label: "Barrios", badge: "green" },
  { value: "bodegas", label: "Bodegas", badge: "amber" },
  { value: "instituciones_educativas", label: "Instituciones educativas", badge: "purple" },
  { value: "centros_salud", label: "Centros de salud", badge: "red" },
  { value: "bancos", label: "Bancos", badge: "teal" },
  { value: "edificio", label: "Edificios", badge: "blue" },
  { value: "retail_supermercados", label: "Retail / supermercados", badge: "amber" },
  { value: "custodia_transito", label: "Custodia en tránsito", badge: "teal" },
  { value: "obra", label: "Obra", badge: "gray" },
];

export const categoryMeta = (value) => CLIENT_CATEGORIES.find((c) => c.value === value);

// "Pendiente de configuración" = sin ningún supervisor asignado y/o sin
// supervisor responsable del SOS definido. Sirve para que el admin sepa a
// qué objetivos volver una vez que termine de cargar el equipo.
export const isUnconfigured = (c) => (c.assigned_supervisor_ids || []).length === 0 || !c.sos_supervisor_id;

const EMPTY_FORM = { name: "", address: "", contact_name: "", contact_email: "", contact_email2: "", contact_email3: "", contact_phone: "", corporate_phone: "", category: "", assigned_supervisor_ids: [], sos_supervisor_id: "", sos_contact_phone: "", legal_name: "", cuit: "", contract_shifts: [], contract_total_hours: "", standard_workday_hours: "", no_service_on_holidays: false };

function shiftsSummary(shifts) {
  if (!shifts || shifts.length === 0) return "";
  return shifts.map((s) => `${s.label || "Turno"} ${s.time_from}-${s.time_to} x${s.staff_count || 1}`).join("; ");
}

function exportClientsCsv(list) {
  const header = ["Nombre", "Razón social", "CUIT", "Categoría", "Dirección", "Franjas horarias", "Horas contratadas (por día)", "Cobertura 24hs", "Teléfono contacto", "Email contacto"];
  const rows = list.map((c) => [
    c.name, c.legal_name || "", c.cuit || "", categoryMeta(c.category)?.label || "", c.address || "",
    shiftsSummary(c.contract_shifts), c.contract_total_hours ?? avgDailyHours(c.contract_shifts) ?? "", c.contract_is_24h ? "Sí" : "No", c.contact_phone || "", c.contact_email || "",
  ]);
  const csv = [header, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = `clientes_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export default function AdminClients() {
  const [list, setList] = useState(null);
  const [supervisors, setSupervisors] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("todos");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ supervisor_id: "", set_as_sos: false });
  const [bulkBusy, setBulkBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePreview, setDeletePreview] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const basePath = isAdmin ? "/admin" : "/supervisor";

  // Fix W (2026-09-11): supervisors ya viene de /users/roster?role=supervisor
  // (activos únicamente) -- se usa para filtrar los ids obsoletos que puedan
  // quedar en assigned_supervisor_ids de un cliente.
  const activeSupervisorIds = useMemo(() => new Set(supervisors.map((s) => s.id)), [supervisors]);
  const supervisorNameById = useMemo(() => new Map(supervisors.map((s) => [s.id, s.name])), [supervisors]);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([
        api.get("/clients?include_inactive=true"),
        api.get("/users/roster?role=supervisor").catch(() => ({ data: [] })),
      ]);
      setList(c.data);
      setSupervisors(s.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = (list || [])
    .filter((c) => c.name.toLowerCase().includes(q.toLowerCase()))
    .filter((c) => catFilter === "todos" ? true : catFilter === "sin_configurar" ? isUnconfigured(c) : c.category === catFilter)
    .sort((a, b) => (a.category || "zzz").localeCompare(b.category || "zzz") || a.name.localeCompare(b.name));

  const unconfiguredCount = (list || []).filter(isUnconfigured).length;

  const toggleSupervisor = (sid) => {
    setForm((f) => ({
      ...f,
      assigned_supervisor_ids: f.assigned_supervisor_ids.includes(sid)
        ? f.assigned_supervisor_ids.filter((x) => x !== sid)
        : [...f.assigned_supervisor_ids, sid],
    }));
  };

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/clients", {
        ...form, category: form.category || null, sos_supervisor_id: form.sos_supervisor_id || null, sos_contact_phone: form.sos_contact_phone || null,
        legal_name: form.legal_name || null, cuit: form.cuit || null,
        contract_shifts: (form.contract_shifts || []).filter((s) => s.time_from && s.time_to),
        contract_total_hours: form.contract_total_hours === "" ? null : Number(form.contract_total_hours),
        standard_workday_hours: form.standard_workday_hours === "" ? null : Number(form.standard_workday_hours),
      });
      toast.success("Cliente creado");
      if (data.phone_warnings) Object.values(data.phone_warnings).forEach((w) => toast.info(w));
      setOpen(false); setForm(EMPTY_FORM);
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const toggleSelect = (cid) => setSelected((s) => s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]);
  const clearSelection = () => setSelected([]);

  const openDeletePreview = async (client) => {
    setDeleteTarget(client);
    setDeleteText("");
    setDeleteBusy(false);
    try {
      const { data } = await api.get(`/clients/${client.id}/delete-preview`);
      setDeletePreview(data);
    } catch (e) { toast.error(formatApiError(e)); setDeleteTarget(null); }
  };

  const closeDeleteModal = () => { setDeleteTarget(null); setDeletePreview(null); setDeleteText(""); };

  const confirmDelete = async (e) => {
    e.preventDefault();
    if (deleteText.trim().toLowerCase() !== deleteTarget.name.trim().toLowerCase()) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/clients/${deleteTarget.id}`);
      toast.success("Cliente eliminado definitivamente");
      closeDeleteModal();
      setSelected((s) => s.filter((x) => x !== deleteTarget.id));
      load();
    } catch (e2) { toast.error(formatApiError(e2)); setDeleteBusy(false); }
  };

  const submitBulkAssign = async (e) => {
    e.preventDefault();
    if (!bulkForm.supervisor_id) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/clients/bulk-assign-supervisor", {
        client_ids: selected, supervisor_id: bulkForm.supervisor_id, set_as_sos: bulkForm.set_as_sos,
      });
      toast.success(`Supervisor asignado a ${data.updated} objetivo(s)`);
      setBulkOpen(false); setBulkForm({ supervisor_id: "", set_as_sos: false }); clearSelection();
      load();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBulkBusy(false); }
  };

  return (
    <div style={{ padding: "20px 24px 60px" }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16, marginBottom: 22 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: "0.2em", color: "#BF3437", fontWeight: 700, textTransform: "uppercase" }}>Clientes</div>
          <h1 style={{ fontSize: 32, fontWeight: 900, margin: "4px 0 0", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Tus sitios protegidos</h1>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)} data-testid="client-new"><Plus size={14} style={{ marginRight: 6 }} />Nuevo cliente</button>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>
        <button className="btn-ghost" onClick={() => exportClientsCsv(filtered)} style={{ padding: "8px 14px", fontSize: 11 }} data-testid="clients-export-csv"><FileDown size={13} style={{ marginRight: 6 }} />Exportar CSV</button>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", maxWidth: 320, flex: "1 1 240px" }}>
          <Search size={16} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#6B655A" }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente..." style={{ paddingLeft: 40, padding: "10px 14px 10px 40px", border: "1.5px solid var(--invict-bone-3)", borderRadius: 2, width: "100%", background: "white", fontSize: 14 }} data-testid="client-search"/>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "nowrap", overflowX: "auto", paddingBottom: 4, maxWidth: "100%" }} data-testid="client-category-filters">
          {[{ value: "todos", label: "Todos" }, ...CLIENT_CATEGORIES].map((c) => (
            <button key={c.value} onClick={() => setCatFilter(c.value)}
              className={catFilter === c.value ? "btn-primary" : "btn-ghost"}
              style={{ padding: "8px 14px", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap", flexShrink: 0 }}
              data-testid={`client-filter-${c.value}`}
            >{c.label}</button>
          ))}
          <button onClick={() => setCatFilter("sin_configurar")}
            className={catFilter === "sin_configurar" ? "btn-primary" : "btn-ghost"}
            style={{ padding: "8px 14px", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap", flexShrink: 0, color: catFilter === "sin_configurar" ? undefined : "#BF3437", borderColor: catFilter === "sin_configurar" ? undefined : "#BF3437" }}
            data-testid="client-filter-sin_configurar"
          ><AlertCircle size={11} style={{ marginRight: 4, display: "inline", verticalAlign: "text-top" }} />Sin configurar{unconfiguredCount > 0 ? ` (${unconfiguredCount})` : ""}</button>
        </div>
      </div>

      {isAdmin && selected.length > 0 && (
        <div className="card" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 16px", marginBottom: 12, background: "rgba(191,52,55,0.05)", border: "1.5px solid rgba(191,52,55,0.25)" }} data-testid="client-bulk-bar">
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0A0A0A" }}>{selected.length} objetivo(s) seleccionado(s)</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn-ghost" onClick={clearSelection} style={{ padding: "7px 12px", fontSize: 11 }} data-testid="client-bulk-clear">Cancelar</button>
            <button className="btn-primary" onClick={() => setBulkOpen(true)} style={{ padding: "7px 12px", fontSize: 11 }} data-testid="client-bulk-assign-open"><Users2 size={12} style={{ marginRight: 5 }}/>Asignar supervisor</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        {list == null && <div style={{ padding: 20 }}><div className="skel" style={{ height: 60 }} /></div>}
        {list && filtered.length === 0 && <div style={{ padding: 40, textAlign: "center", color: "var(--invict-text-muted)" }}>Ningún cliente coincide con el filtro.</div>}
        {list && filtered.map((c) => {
          const meta = categoryMeta(c.category);
          const pending = isUnconfigured(c);
          return (
            <div key={c.id} style={{ display: "grid", gridTemplateColumns: "auto 2fr 2fr 1fr 2fr 1fr auto", gap: 12, padding: "16px 20px", borderBottom: "1px solid var(--invict-bone-3)", alignItems: "center" }} data-testid={`client-row-${c.id}`}>
              <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} data-testid={`client-select-${c.id}`} style={{ width: 16, height: 16, cursor: "pointer" }} />
              <Link to={`${basePath}/clientes/${c.id}`} style={{ color: "inherit", textDecoration: "none" }}>
                <div style={{ fontWeight: 800, fontSize: 14, color: "#0A0A0A" }}>{c.name}</div>
                <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>{c.address || "—"}</div>
              </Link>
              <Link to={`${basePath}/clientes/${c.id}`} style={{ color: "inherit", textDecoration: "none", fontSize: 12 }}>{c.contact_name || "—"}<div style={{ color: "var(--invict-text-muted)" }}>{c.contact_email || c.contact_phone || ""}</div></Link>
              <div>{meta ? <span className={`badge ${meta.badge}`} data-testid={`client-category-badge-${c.id}`}>{meta.label}</span> : <span style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>Sin categoría</span>}</div>
              <div style={{ fontSize: 11 }}>
                {pending
                  ? <span className="badge amber" data-testid={`client-pending-badge-${c.id}`}><AlertCircle size={10} style={{ marginRight: 3, display: "inline", verticalAlign: "text-top" }}/>Pendiente de configurar</span>
                  : (() => {
                      // Fix W (2026-09-11): contar solo supervisores activos y sin
                      // repetidos -- antes sumaba cuentas duplicadas/desactivadas
                      // (ids viejos que ya no son de un supervisor vigente).
                      const activeIds = (c.assigned_supervisor_ids || []).filter((id) => activeSupervisorIds.has(id));
                      const names = activeIds.map((id) => supervisorNameById.get(id)).filter(Boolean);
                      return (
                        <span style={{ color: "var(--invict-text-muted)" }} title={names.length ? names.join(", ") : undefined} data-testid={`client-supervisor-count-${c.id}`}>
                          <UserCheck size={11} style={{ marginRight: 3, display: "inline", verticalAlign: "text-top", color: "#16a34a" }}/>{activeIds.length} supervisor(es)
                        </span>
                      );
                    })()}
              </div>
              <div><span className={`badge ${c.active ? "green" : "gray"}`}>{c.active ? "Activo" : "Inactivo"}</span></div>
              {isAdmin && <button type="button" onClick={() => openDeletePreview(c)} className="btn-ghost" title="Eliminar cliente definitivamente" style={{ padding: "6px 10px", fontSize: 10, color: "#BF3437", borderColor: "#BF3437" }} data-testid={`client-delete-row-${c.id}`}><Trash2 size={12}/></button>}
              {!isAdmin && <div />}
            </div>
          );
        })}
      </div>

      {open && (
        <div className="modal-overlay" onClick={() => setOpen(false)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="client-modal">
            <h3>Nuevo cliente</h3>
            <p>Cargá los datos del sitio que vas a proteger.</p>
            <div className="field"><label>Nombre del cliente *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name"/></div>
            <div className="field"><label>Categoría</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="client-category-select">
                <option value="">Sin categoría</option>
                {CLIENT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="field"><label>Dirección</label><input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-address"/></div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="field"><label>Contacto</label><input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></div>
              <div className="field"><label>Email</label><input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="client-email"/></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="field"><label>Email adicional</label><input type="email" value={form.contact_email2} onChange={(e) => setForm({ ...form, contact_email2: e.target.value })} data-testid="client-email2"/></div>
              <div className="field"><label>Email adicional</label><input type="email" value={form.contact_email3} onChange={(e) => setForm({ ...form, contact_email3: e.target.value })} data-testid="client-email3"/></div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div className="field"><label>Teléfono de contacto</label><input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="ej. 261 516 9800" data-testid="client-phone"/></div>
              <div className="field"><label>Teléfono corporativo del establecimiento</label><input value={form.corporate_phone} onChange={(e) => setForm({ ...form, corporate_phone: e.target.value })} placeholder="ej. 261 400 0000" data-testid="client-corporate-phone"/></div>
            </div>
            <div className="card" style={{ padding: 14, margin: "4px 0 14px", background: "var(--invict-bone-2)" }} data-testid="client-contract-block">
              <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#0A0A0A", marginBottom: 8 }}>Datos del servicio contratado</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <div className="field"><label>Razón social</label><input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="client-legal-name"/></div>
                <div className="field"><label>CUIT</label><input value={form.cuit} onChange={(e) => setForm({ ...form, cuit: e.target.value })} placeholder="20-30405060-7" data-testid="client-cuit"/></div>
              </div>
              <ContractShiftsEditor shifts={form.contract_shifts} onChange={(s) => setForm({ ...form, contract_shifts: s })} totalOverride={form.contract_total_hours} onTotalOverrideChange={(v) => setForm({ ...form, contract_total_hours: v })} testIdPrefix="client-shift" noServiceOnHolidays={form.no_service_on_holidays} onNoServiceOnHolidaysChange={(v) => setForm({ ...form, no_service_on_holidays: v })}/>
              <div className="field" style={{ marginTop: 10 }}>
                <label>Jornada estándar (hs) <span style={{ fontWeight: 400, color: "var(--invict-text-muted)", textTransform: "none", letterSpacing: 0 }}>(opcional — para calcular horas extra en "Horas Extra")</span></label>
                <input type="number" step="0.5" min="0" value={form.standard_workday_hours} onChange={(e) => setForm({ ...form, standard_workday_hours: e.target.value })} placeholder="ej. 12 (default INVICT)" data-testid="client-standard-workday-hours"/>
                <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "4px 0 0" }}>Especialmente útil en objetivos tipo "Obra" (sin turnos fijos): lo que exceda esta jornada se calcula como hora extra.</p>
              </div>
            </div>
            <div className="field">
              <label>Supervisores asignados <span style={{ fontWeight: 400, color: "var(--invict-text-muted)", textTransform: "none", letterSpacing: 0 }}>(opcional)</span></label>
              <p style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "0 0 8px" }}>Podés crear el objetivo ahora y asignar el supervisor cuando lo tengas cargado.</p>
              {supervisors.length === 0 ? (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#0A0A0A", background: "var(--invict-bone-2)", border: "1.5px dashed var(--invict-bone-3)", borderRadius: 2, padding: 10 }} data-testid="client-no-supervisors-hint">
                  <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1, color: "#BF3437" }} />
                  <span>Todavía no cargaste supervisores. Podés crear este objetivo igual y volver más tarde a asignarlo. <Link to="/admin/usuarios" style={{ color: "#BF3437", fontWeight: 700 }} data-testid="client-goto-users-link">Crear usuarios →</Link></span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 140, overflowY: "auto", border: "1.5px solid var(--invict-bone-3)", borderRadius: 2, padding: 8 }} data-testid="client-supervisors-list">
                  {supervisors.map((s) => (
                    <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                      <input type="checkbox" checked={form.assigned_supervisor_ids.includes(s.id)} onChange={() => toggleSupervisor(s.id)} data-testid={`client-supervisor-checkbox-${s.id}`}/>
                      {s.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="field">
              <label>Supervisor responsable del SOS</label>
              <select value={form.sos_supervisor_id} onChange={(e) => setForm({ ...form, sos_supervisor_id: e.target.value })} data-testid="client-sos-supervisor-select">
                <option value="">Sin definir (usa el primer supervisor asignado)</option>
                {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div className="field"><label>Teléfono de contacto SOS</label><input value={form.sos_contact_phone} onChange={(e) => setForm({ ...form, sos_contact_phone: e.target.value })} placeholder="ej. +54 9 261 000 0000" data-testid="client-sos-phone"/></div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="client-submit">{busy ? "Guardando..." : "Crear cliente"}</button>
            </div>
          </form>
        </div>
      )}

      {bulkOpen && (
        <div className="modal-overlay" onClick={() => setBulkOpen(false)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submitBulkAssign} data-testid="client-bulk-modal">
            <h3>Asignar supervisor a {selected.length} objetivo(s)</h3>
            <p style={{ fontSize: 13 }}>Se agrega este supervisor a la lista de asignados de cada objetivo seleccionado (no reemplaza a los que ya tenga).</p>
            <div className="field"><label>Supervisor *</label>
              <select required value={bulkForm.supervisor_id} onChange={(e) => setBulkForm({ ...bulkForm, supervisor_id: e.target.value })} data-testid="client-bulk-supervisor-select">
                <option value="">Elegí un supervisor</option>
                {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 8 }}>
              <input type="checkbox" checked={bulkForm.set_as_sos} onChange={(e) => setBulkForm({ ...bulkForm, set_as_sos: e.target.checked })} data-testid="client-bulk-set-sos"/>
              También asignarlo como responsable del SOS en estos objetivos
            </label>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={() => setBulkOpen(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={bulkBusy || !bulkForm.supervisor_id} data-testid="client-bulk-submit">{bulkBusy ? "Asignando..." : "Asignar"}</button>
            </div>
          </form>
        </div>
      )}
      {deleteTarget && deletePreview && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={confirmDelete} data-testid="client-delete-modal">
            <h3 style={{ color: "#BF3437", display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={18}/>Eliminar "{deletePreview.client_name}" definitivamente</h3>
            <p style={{ fontSize: 13 }}>
              Esta acción es <b>irreversible</b>. Se van a borrar <b>{deletePreview.total_records}</b> registros asociados a este objetivo:
            </p>
            <div style={{ background: "var(--invict-bone-2)", padding: 12, borderRadius: 2, marginBottom: 12, fontSize: 12, lineHeight: 1.7 }} data-testid="client-delete-counts">
              <div>Puntos de ronda + QR: <b>{deletePreview.patrol_points}</b></div>
              <div>Rondas: <b>{deletePreview.patrols}</b> · Escaneos: <b>{deletePreview.patrol_scans}</b></div>
              <div>Novedades: <b>{deletePreview.news}</b> · Servicios: <b>{deletePreview.services}</b> · Marcaciones: <b>{deletePreview.supervisor_checkins}</b></div>
              <div>Alertas de pánico: <b>{deletePreview.panic_alerts}</b></div>
              <div>Elementos de trabajo: <b>{deletePreview.material_items + deletePreview.material_handoffs}</b> · Personal autorizado: <b>{deletePreview.authorized_persons}</b></div>
              <div>Vehículos: <b>{deletePreview.vehicle_logs}</b> · Visitas: <b>{deletePreview.visit_logs}</b> · Mensajes de chat: <b>{deletePreview.chat_messages}</b></div>
              <div>Sedes cargadas: <b>{deletePreview.client_sites}</b> · Turnos de cronograma: <b>{deletePreview.schedule_assignments}</b></div>
              {deletePreview.users_with_default_target > 0 && (
                <div style={{ marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--invict-bone-3)", color: "#BF3437", fontWeight: 700 }}>
                  <AlertCircle size={12} style={{ marginRight: 4, display: "inline", verticalAlign: "text-top" }}/>
                  {deletePreview.users_with_default_target} usuario(s) tienen este objetivo como asignado por defecto. Se les va a quitar para que ningún SOS quede sin destino.
                </div>
              )}
            </div>
            <div className="field">
              <label>Para confirmar, escribí el nombre exacto: <b>{deletePreview.client_name}</b></label>
              <input value={deleteText} onChange={(e) => setDeleteText(e.target.value)} autoFocus data-testid="client-delete-confirm-input"/>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <button type="button" className="btn-ghost" onClick={closeDeleteModal}>Cancelar</button>
              <button type="submit" className="btn-primary" style={{ background: "#BF3437" }}
                disabled={deleteBusy || deleteText.trim().toLowerCase() !== deletePreview.client_name.trim().toLowerCase()}
                data-testid="client-delete-confirm-submit">
                {deleteBusy ? "Eliminando..." : "Eliminar definitivamente"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
